import { Link, useSearchParams } from "react-router-dom";
import { SiteFooter } from "../components/SiteFooter";

const COLOR = {
  bg: "#FAF7F0",
  ink: "#2A1E5C",
  ink2: "#5D5A6B",
  muted: "#8B7E6A",
  line: "#E8E2D5",
  card: "#FFFFFF",
  gold: "#F5CB5C",
};

const STEPS = [
  { n: "1", title: "We're building your sheet", desc: "Your custom Payday Relay is being cloned and your Relay Calculator numbers plugged in. No work needed on your end." },
  { n: "2", title: "Check your inbox", desc: "Your personal setup link lands within a couple of minutes. Open it to connect Google and claim your sheet." },
  { n: "3", title: "Run your first relay", desc: "Pick your start date, and your Weekly Target goes live. Red → Black → Green." },
];

export default function ThankYouPage() {
  const [searchParams] = useSearchParams();
  const product = (searchParams.get("product") || "").toLowerCase();
  // Bundle buyers already have the app → show the coaching Pit Crew (waitlist) instead.
  const alreadyHasCaddy = product === "income_map_caddy_bundle" || product === "pit_crew_bundle" ||
    product === "free_sprint" || product.startsWith("season");

  return (
    <div className="min-h-screen flex flex-col" style={{ background: COLOR.bg }}>
      <header className="py-2 px-4 sm:px-6 border-b" style={{ borderColor: COLOR.line, background: COLOR.card }}>
        <div className="max-w-3xl mx-auto flex items-center justify-center">
          <Link to="/">
            <img src="/images/paydayrelaylogo-tight.png" alt="Payday Relay" className="h-[64px] sm:h-[81px] w-auto" data-testid="thankyou-header-logo" />
          </Link>
        </div>
      </header>

      <main className="flex-1 px-6 py-12">
        <div className="max-w-2xl mx-auto">
          {/* Concierge hero */}
          <div className="text-center mb-10">
            <h1 className="text-4xl sm:text-5xl mb-4" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
              You're in!
            </h1>
            <p className="text-lg sm:text-xl font-semibold" style={{ color: COLOR.ink }} data-testid="thankyou-concierge-message">
              Sit back, we're building your custom Payday Relay sheet now.
            </p>
          </div>

          {/* What happens next */}
          <div className="rounded-2xl p-8 border mb-8" style={{ background: COLOR.card, borderColor: COLOR.line }} data-testid="thankyou-steps">
            <h2 className="text-lg font-bold mb-6" style={{ color: COLOR.ink }}>What happens next</h2>
            <div className="space-y-5">
              {STEPS.map((s) => (
                <div key={s.n} className="flex items-start gap-4">
                  <span
                    className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0"
                    style={{ background: COLOR.gold, color: COLOR.ink }}
                  >
                    {s.n}
                  </span>
                  <div>
                    <p className="font-bold text-sm mb-0.5" style={{ color: COLOR.ink }}>{s.title}</p>
                    <p className="text-sm leading-relaxed" style={{ color: COLOR.ink2 }}>{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* The Bridge, post-purchase upsell */}
          {alreadyHasCaddy ? (
            /* Bundle buyers already have the app, tease the free weekly Q&A calls */
            <div className="rounded-2xl p-8 mb-8" style={{ background: COLOR.ink }} data-testid="thankyou-pitcrew-upsell">
              <p className="text-[11px] font-bold tracking-widest uppercase mb-2" style={{ color: COLOR.gold }}>
                Coming soon, included with your race
              </p>
              <h2 className="text-2xl sm:text-3xl mb-3" style={{ fontFamily: "'Fraunces', Georgia, serif", color: "#FFFFFF" }}>
                Weekly Q&amp;A live calls
              </h2>
              <p className="text-sm leading-relaxed mb-5" style={{ color: "rgba(255,255,255,0.85)" }}>
                You've got the whole Payday Relay system. Next up: a standing weekly call to talk through income
                pain points, get a little coaching, and learn how to get the most out of Payday Relay.
                <b> Free for Payday Relay customers.</b>
              </p>
              <ul className="space-y-2 mb-6 text-sm" style={{ color: "rgba(255,255,255,0.9)" }}>
                <li>💬 <b>Talk income pain points:</b> slow months, off targets, payday-rhythm puzzles</li>
                <li>🧭 <b>A little coaching:</b> practical nudges to keep your rhythm going</li>
                <li>🛠️ <b>Get the most out of the app:</b> tools, hacks &amp; habits from racers like you</li>
                <li>📼 <b>Replays included:</b> catch every session, even the ones you miss</li>
              </ul>
              <p className="text-xs mb-2" style={{ color: "rgba(255,255,255,0.6)" }}>
                Nothing to do yet. The calls will show up in your dashboard when they open.
              </p>
              <p className="text-center mt-4">
                <Link to="/dashboard" className="text-xs underline" style={{ color: "rgba(255,255,255,0.7)" }} data-testid="thankyou-pit-crew-info-link">
                  Open my race dashboard →
                </Link>
              </p>
            </div>
          ) : (
            /* Legacy buyers without a race yet, point them at the Season Pass */
            <div className="rounded-2xl p-8 mb-8" style={{ background: COLOR.ink }} data-testid="thankyou-caddy-upsell">
              <p className="text-[11px] font-bold tracking-widest uppercase mb-2" style={{ color: COLOR.gold }}>
                Run the whole race
              </p>
              <h2 className="text-2xl sm:text-3xl mb-3" style={{ fontFamily: "'Fraunces', Georgia, serif", color: "#FFFFFF" }}>
                Add your <span style={{ color: COLOR.gold }}>Season Pass</span>
              </h2>
              <p className="text-sm leading-relaxed mb-5" style={{ color: "rgba(255,255,255,0.85)" }}>
                Your sheet is the scoreboard. The Season Pass is the race around it: log a win in seconds
                from your phone or laptop, and your sheet keeps itself up to date. The sheet is yours to
                keep either way.
              </p>
              <ul className="space-y-2 mb-6 text-sm" style={{ color: "rgba(255,255,255,0.9)" }}>
                <li>📱 <b>Daily logging</b>, in seconds from your phone or your laptop</li>
                <li>🔄 <b>Automatic sync</b>, your sheet updates itself</li>
                <li>🛠️ <b>Quarterly Tax Days</b>, review the quarter you ran and plan the next one</li>
                <li>🧰 <b>Pit Crew tools</b>, the Debt Strategist, Savings Goal Splitter and PTO Buffer Planner</li>
              </ul>
              <p className="text-2xl font-bold mb-1" style={{ color: COLOR.gold }}>$19/mo</p>
              <p className="text-xs mb-5" style={{ color: "rgba(255,255,255,0.6)" }}>
                or $119 a year · cancel anytime
              </p>
              <Link
                to="/start/season-pass"
                className="block w-full py-3.5 rounded-full font-bold text-base text-center transition-transform hover:scale-[1.02]"
                style={{ background: COLOR.gold, color: COLOR.ink }}
                data-testid="thankyou-caddy-cta"
              >
                See Season Pass →
              </Link>
              <p className="text-center mt-4">
                <Link to="/dashboard" className="text-xs underline" style={{ color: "rgba(255,255,255,0.7)" }} data-testid="thankyou-pro-link">
                  Open my race dashboard →
                </Link>
              </p>
            </div>
          )}

          <p className="text-xs text-center" style={{ color: COLOR.muted }}>
            Didn't get your setup email within 5 minutes? Check spam, or write us at support@paydayrelay.com, we'll sort it fast.
          </p>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
